import { useEffect, useLayoutEffect, useState } from "react";
import type { Theme } from "../theme/pixel";

interface Pair {
  attackerId: number;
  blockerId: number;
}

interface Props {
  container: HTMLDivElement | null;
  cardRefs: Map<number, HTMLDivElement | null>;
  pairs: Pair[];
  /** Drives a dangling line from this card to the cursor. */
  pendingBlockerId: number | null;
  theme: Theme;
}

export function BlockLines({ container, cardRefs, pairs, pendingBlockerId, theme }: Props) {
  const [bounds, setBounds] = useState<DOMRect | null>(null);
  const [, setTick] = useState(0);
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);

  useLayoutEffect(() => {
    if (!container) return;
    setBounds(container.getBoundingClientRect());
    const bump = () => setTick((t) => t + 1);
    window.addEventListener("resize", bump);
    // Capture-phase scroll listener — `scroll` doesn't bubble, so a
    // non-capture window listener would miss inner-container scrolls
    // (each Field is independently `overflow-x: auto`).
    document.addEventListener("scroll", bump, true);
    return () => {
      window.removeEventListener("resize", bump);
      document.removeEventListener("scroll", bump, true);
    };
  }, [container, pairs.length]);

  // Track the cursor continuously, not just while a blocker is pinned.
  //
  // Older logic registered the mousemove listener only when
  // `pendingBlockerId` was non-null, so the dangler line stayed hidden
  // until the user moved the mouse after clicking — a still cursor
  // produced no event, no `cursor` state, and the conditional dangler
  // branch below skipped rendering. Click-and-drag worked only because
  // the drag itself produces mousemove events. Listening always keeps
  // `cursor` populated so the dangler renders on the first paint after
  // the click, regardless of how the user pinned the blocker.
  useEffect(() => {
    const handler = (e: MouseEvent) => setCursor({ x: e.clientX, y: e.clientY });
    window.addEventListener("mousemove", handler);
    return () => window.removeEventListener("mousemove", handler);
  }, []);

  if (!container || !bounds) return null;

  const lines = pairs.flatMap(({ attackerId, blockerId }) => {
    const a = cardRefs.get(attackerId);
    const b = cardRefs.get(blockerId);
    if (!a || !b) return [];
    const ar = a.getBoundingClientRect();
    const br = b.getBoundingClientRect();
    const x1 = ar.left + ar.width / 2 - bounds.left;
    const y1 = ar.top + ar.height / 2 - bounds.top;
    const x2 = br.left + br.width / 2 - bounds.left;
    const y2 = br.top + br.height / 2 - bounds.top;
    return [{ key: `${attackerId}-${blockerId}`, x1, y1, x2, y2 }];
  });

  let dangler: { x1: number; y1: number; x2: number; y2: number } | null = null;
  if (pendingBlockerId !== null && cursor) {
    const el = cardRefs.get(pendingBlockerId);
    if (el) {
      const r = el.getBoundingClientRect();
      dangler = {
        x1: r.left + r.width / 2 - bounds.left,
        y1: r.top + r.height / 2 - bounds.top,
        x2: cursor.x - bounds.left,
        y2: cursor.y - bounds.top,
      };
    }
  }

  return (
    <svg
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 10,
      }}
      width={bounds.width}
      height={bounds.height}
    >
      {lines.map((l) => (
        <g key={l.key}>
          <line
            x1={l.x1}
            y1={l.y1}
            x2={l.x2}
            y2={l.y2}
            stroke={theme.bgDeep}
            strokeWidth={6}
            strokeLinecap="square"
          />
          <line
            x1={l.x1}
            y1={l.y1}
            x2={l.x2}
            y2={l.y2}
            stroke={theme.accent}
            strokeWidth={3}
            strokeDasharray="6,4"
            strokeLinecap="square"
          />
        </g>
      ))}
      {dangler && (
        <g>
          <line
            x1={dangler.x1}
            y1={dangler.y1}
            x2={dangler.x2}
            y2={dangler.y2}
            stroke={theme.bgDeep}
            strokeWidth={6}
            strokeLinecap="square"
          />
          <line
            x1={dangler.x1}
            y1={dangler.y1}
            x2={dangler.x2}
            y2={dangler.y2}
            stroke={theme.accent}
            strokeWidth={3}
            strokeDasharray="6,4"
            strokeLinecap="square"
            opacity={0.7}
          />
        </g>
      )}
    </svg>
  );
}
